import mainStyle from "../../../mainStyle.module.scss";

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Typography,
  RadioGroup,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from 'lodash';

import { formLimitation } from "const";
import CollapseCard from "components/Collapse";
import { MenuSelect } from "components/Select/menuSelect";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { RadioButton } from 'components/buttons/RadioButton';
import { Loading } from 'components/Loading';

// Helper
import {
  getPortMediaType,
} from "./helper";

const rowStyle = {
  display: "flex",
  gap: 3,
  alignItems: "center",
  width: "100%",
};

const checkPvid = (value) => {
  if (value === "") {
    return false;
  }

  if (!/^\d+$/.test(value)) {
    return false;
  }

  const intValue = Number(value);
  if (intValue < 1 || intValue > 4094) {
    return false;
  }

  return true;
};

const checkAllowVlans = (value) => {
  if (!value?.trim()) return false;

  const parts = value.split(",").map((s) => s.trim());
  const used = new Set();
  const { min, max } = formLimitation.vlanId;

  for (let part of parts) {
    if (part === "") return false;

    if (part.includes("-")) {
      const [startStr, endStr] = part.split("-").map((s) => s.trim());
      const start = parseInt(startStr, 10);
      const end = parseInt(endStr, 10);

      if (isNaN(start) || isNaN(end) || start < min || end > max || start > end)
        return false;

      for (let i = start; i <= end; i++) {
        if (used.has(i)) return false;
        used.add(i);
      }
    } else {
      const num = parseInt(part, 10);
      if (isNaN(num) || num < min || num > max) return false;

      if (used.has(num)) return false;
      used.add(num);
    }
  }

  return true;
};

const SettingsDrawer = ({
  drawerOpen,
  setAlertInfo,
  linkSpeedOptions,
  activeRow,
  setActiveRow,
  originalRowData,
  setOriginalRowData,
  switchesData,
  setSwitchesData,
  onUpdateBlockerData,
  profileSwitchList,
  getSupportedSpeedOptions,
  showEditGeneral,
  isSelectedPortSupportPoE,
  vlanIdsCount,
}) => {
  const theme = useTheme();
  const [selectedProfile, setSelectedProfile] = useState(null);
  const isProfileMode = useMemo(() => !selectedProfile?.isDefault, [selectedProfile?.isDefault]);
  const { isCopper, isFiber, linkSpeedLabel } = useMemo(() => getPortMediaType({ activeRow }), [activeRow]);

  const linkSpeedData = useMemo(
    () => linkSpeedOptions.sort((a, b) => a.id - b.id),
    [linkSpeedOptions]
  );

  const [isLoading, setIsLoading] = useState(false);
  const [updateUuid, setUpdateUuid] = useState(uuidv4());
  const [isChanged, setIsChanged] = useState(false);
  const isEditable = useMemo(() => !selectedProfile || selectedProfile?.isDefault, [selectedProfile]);

  const supportedSpeed = getSupportedSpeedOptions(
    activeRow.linkSpeed,
    activeRow.portIndex
  );
  const breadcrumbs = JSON.parse(localStorage.getItem("selectBreadcrumbs"));
  const hasManageOrg = breadcrumbs.realm?.hasManageOrg === true;
  const realmId = breadcrumbs.realm?.id ?? null;
  const orgsId = breadcrumbs.orgs?.id ?? null;
  const siteId = breadcrumbs.site?.id ?? null;
  let entId = null;
  let orgId = null;

  if (hasManageOrg) {
    entId = realmId;
    if (orgsId) orgId = orgsId;
  } else {
    orgId = realmId;
  }

  const isValidInputStyles = (isValid) => ({
    width: "64.5%",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.customBorderRadius[1],
      height: "31px",
      backgroundColor: theme.palette.primary.transparent,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.borderGreyPrimary
          : theme.palette.primary.redPrimary,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.mainHover
          : theme.palette.primary.redPrimary,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.main
          : theme.palette.primary.redPrimary,
      },
    },
  });

  const checkFormValid = () => {
    if (
      activeRow.portType === "trunk" &&
      !checkAllowVlans(activeRow.allowVlan?.trim())
    ) {
      return false;
    }

    if (!checkPvid(activeRow.PVID?.toString().trim())) {
      return false;
    }

    if (vlanIdsCount.number > 128) {
      return false;
    }

    if (supportedSpeed.s.length > 0 && activeRow.speedS == null) {
      return false;
    }

    if (supportedSpeed.t.length > 0 && activeRow.speedT == null) {
      return false;
    }

    return true;
  };

  const handleCancel = () => {
    setActiveRow(originalRowData);

    const matchedProfile = profileSwitchList.find(profile => profile.value === originalRowData.portProfile) || null;
    setSelectedProfile(matchedProfile);
  };

  const onSave = () => {
    const updatedPort = getUpdatedPort();
    const newSwitchPortsData = switchesData.map(loopSwitch => {

      if (updatedPort.id === loopSwitch.id) {
        return {
          ...updatedPort,
          isWaitingEdit: true
        }
      } else {
        return loopSwitch;
      }
    })
    setSwitchesData(cloneDeep(newSwitchPortsData));
    onUpdateBlockerData(newSwitchPortsData);
    setOriginalRowData(cloneDeep(activeRow));

    setIsChanged(false);

    setAlertInfo({
      open: true,
      severity: "success",
      message: (
        <Box>
          <Box
            sx={{
              fontSize: theme.typography.customFontSize[3],
              fontWeight: theme.typography.customFontWeight[3],
              pb: 1
            }}
          >
            Changes Saved
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography>Settings saved to draft. Click</Typography>
            <Typography sx={{ fontWeight: theme.typography.customFontWeight[3] }}> "Apply Changes" </Typography>
            <Typography>to deploy them to the device.</Typography>
          </Box>
        </Box>
      )
    });

  };

  const getUpdatedPort = () => {
    const {
      finalSpeed,
      finalComboSfpSpeed
    } = parseRowLinkSpeed();

    const updatedPort = {
      ...activeRow,
      portProfile: selectedProfile ? selectedProfile.value : "",
      speed: finalSpeed,
      comboSfpSpeed: finalComboSfpSpeed,
      operation: {
        type: activeRow.operation?.type,
        mirrorPort: activeRow.operation?.mirrorPort ?? [],
        mirrorMode: activeRow.operation?.mirrorMode ?? "",
        aggregatePort: activeRow.operation?.aggregatePort ?? [],
        aggregateType: activeRow.operation?.aggregateType ?? "",
      },
    }

    return updatedPort;
  };

  const parseRowLinkSpeed = () => {
    // --- Speed 分流 (S/T) ---
    const {
      s: sSpeeds = [],
      t: tSpeeds = []
    } = supportedSpeed || {};

    // 最終 speed（回傳給後端）
    const finalSpeed = sSpeeds.length > 0 ? activeRow.speedS || "" : tSpeeds.length > 0 ? activeRow.speedT || "" : activeRow.speed || "";

    // 若同時支援 S + T，則 T 塞到 comboSfpSpeed
    const finalComboSfpSpeed = sSpeeds.length > 0 && tSpeeds.length > 0 ? activeRow.speedT || "" : undefined;

    return {
      finalSpeed,
      finalComboSfpSpeed
    };
  };

  useEffect(() => {
    if (!drawerOpen) {
      setSelectedProfile(null);
    }
  }, [drawerOpen]);

  useEffect(() => {
    const matchedProfile = profileSwitchList.find(profile => profile.value === activeRow.portProfile) || null;
    setSelectedProfile(matchedProfile);
  }, [activeRow?.portIndex, activeRow?.portProfile]);

  useEffect(() => {
    const changed =
      JSON.stringify(activeRow ?? {}) !== JSON.stringify(originalRowData ?? {});
    setIsChanged(changed);
  }, [activeRow, originalRowData]);

  return (
    <>
      {isLoading && <Loading />}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 2,
          px: 4,
          boxSizing: "border-box",
          gap: "20px",
        }}
      >
        <CollapseCard title="Port">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {activeRow && (
              <>
                {/* Port Name */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Port Name
                  </Typography>
                  <TextField
                    type="text"
                    value={activeRow.portName}
                    /* disabled={true} */ // Port Name is read-only, when API support update, remove this line
                    onChange={(e) =>
                      setActiveRow({
                        ...activeRow,
                        portName: e.target.value,
                      })
                    }
                    sx={isValidInputStyles}
                  />
                </Box>

                {/* Port Profile */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Port Profile
                  </Typography>
                  <MenuSelect
                    value={selectedProfile?.value || ""}
                    disabled={!showEditGeneral}
                    onChange={(opt) => {
                      const profile = profileSwitchList.find((item) => item.value === opt.value) || null;

                      setSelectedProfile(profile);

                      const isSpeedExist = {
                        s: supportedSpeed.s.find(speed => speed.value === profile?.speed),
                        t: supportedSpeed.t.find(speed => speed.value === profile?.speed)
                      }

                      if (profile?.isDefault) {
                        setActiveRow((prev) => ({
                          ...prev,
                          portProfile: opt.value,
                          portProfileName: opt.name,
                        }))
                      } else {
                        setActiveRow((prev) => ({
                          ...prev,
                          portProfile: opt.value,
                          portProfileName: opt.name,
                          allowVlan: profile ? profile.allowVlan : prev.allowVlan,
                          PVID: profile ? profile.PVID : prev.PVID,
                          portType: profile ? profile.portType : prev.portType,
                          portState: profile ? profile.portState : prev.portState,
                          speed: profile ? profile.speed : prev.speed,
                          speedS: profile && isSpeedExist.s ? profile.speed : prev.speed,
                          speedT: profile && isSpeedExist.t ? profile.speed : prev.speed,
                          poeState: profile ? profile.poeState : prev.poeState,
                          ddpState: profile ? profile.ddpState : prev.ddpState,
                          lbdState: profile ? profile.lbdState : prev.lbdState,
                        }));
                      }

                    }}
                    options={profileSwitchList}
                    width="100%"
                  />
                </Box>
                {/* Port State */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Port State
                  </Typography>

                  {
                    isProfileMode &&
                    <RadioGroup
                      row
                      disabled
                      value={selectedProfile?.portState ? "enable" : "disable"}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "80px auto",
                        columnGap: "20px",
                      }}
                    >
                      {["Enable", "Disable"].map((option, idx) => (
                        <RadioButton
                          key={"profilePortStateRadioButton" + idx}
                          label={option}
                          name="profilePortStateRadio"
                          value={option.toLowerCase()}
                          disabled
                        />
                      ))}
                    </RadioGroup>
                  }

                  {
                    !isProfileMode &&
                    <RadioGroup
                      row
                      value={activeRow?.portState ? "enable" : "disable"}
                      onChange={(e) => {
                        const newValue = e.target.value === "enable";
                        setActiveRow((prev) => ({
                          ...prev,
                          portState: newValue,
                        }));
                      }}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "80px auto",
                        columnGap: "20px",
                      }}
                    >
                      {["Enable", "Disable"].map((option, idx) => (
                        <RadioButton
                          key={"portStateRadioButton" + idx}
                          label={option}
                          name="portStateRadio"
                          value={option.toLowerCase()}
                          disabled={!showEditGeneral || !isEditable}
                        />
                      ))}
                    </RadioGroup>
                  }
                </Box>
                {/* Port Type */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Port Type
                  </Typography>

                  {
                    isProfileMode &&
                    <RadioGroup
                      row
                      disabled
                      value={selectedProfile?.portType === "access" ? "access" : "trunk"}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "80px auto",
                        columnGap: "20px",
                      }}
                    >
                      {["Access", "Trunk"].map((option, idx) => (
                        <RadioButton
                          key={"profilePortTypeRadioButton" + idx}
                          label={option}
                          name="profilePortTypeRadio"
                          value={option.toLowerCase()}
                          disabled
                        />
                      ))}
                    </RadioGroup>
                  }

                  {
                    !isProfileMode &&
                    <RadioGroup
                      row
                      value={
                        activeRow?.portType === "access" ? "access" : "trunk"
                      }
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setActiveRow((prev) => ({ ...prev, portType: newValue }));
                      }}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "80px auto",
                        columnGap: "20px",
                      }}
                    >
                      {["Access", "Trunk"].map((option, idx) => (
                        <RadioButton
                          key={"portTypeRadioButton" + idx}
                          label={option}
                          name="portTypeRadio"
                          value={option.toLowerCase()}
                          disabled={!showEditGeneral || !isEditable}
                        />
                      ))}
                    </RadioGroup>
                  }
                </Box>

                {/* PVID */}
                <Box sx={rowStyle}>
                  <Typography
                    className={mainStyle["collapse-typography"]}
                    sx={{ alignSelf: "flex-start", pt: "6px" }}
                  >
                    PVID
                  </Typography>

                  {
                    isProfileMode &&
                    <TextField
                      disabled
                      type="number"
                      value={selectedProfile?.PVID ?? ""}
                      sx={isValidInputStyles(true)}
                      helperText="(1~4094)"
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                    />
                  }

                  {
                    !isProfileMode &&
                    <TextField
                      disabled={!showEditGeneral || !isEditable}
                      type="number"
                      value={activeRow.PVID ?? ""}
                      onChange={(e) =>
                        setActiveRow({
                          ...activeRow,
                          PVID:
                            e.target.value === "" ? "" : Number(e.target.value),
                        })
                      }
                      sx={isValidInputStyles(
                        checkPvid(activeRow.PVID?.toString().trim())
                      )}
                      helperText="(1~4094)"
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                    />
                  }
                </Box>

                {
                  !isProfileMode &&
                  vlanIdsCount.number > 128 &&
                  <>
                    <Box
                      sx={{
                        ...rowStyle,
                        marginTop: "-1.3rem"
                      }}
                    >
                      <Typography
                        className={mainStyle["collapse-typography"]}
                        sx={{ alignSelf: "flex-start", pt: "6px" }}
                      >
                      </Typography>
                      <Typography
                        sx={{
                          color: theme.palette.primary.redPrimary,
                          fontSize: "0.75rem",
                        }}
                      >
                        Limit reached: Maximum 128 VLANs allowed.
                      </Typography>

                    </Box>
                  </>
                }

                {/* Allow VLAN */}
                {
                  selectedProfile?.portType === "trunk" &&
                  isProfileMode &&
                  <Box sx={rowStyle}>
                    <Typography
                      className={mainStyle["collapse-typography"]}
                      sx={{ alignSelf: "flex-start", pt: "6px" }}
                    >
                      Allow VLAN
                    </Typography>

                    <TextField
                      disabled
                      type="text"
                      placeholder="e.g. 1,4-7"
                      value={selectedProfile?.allowVlan}
                      sx={isValidInputStyles(true)}
                      helperText={`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                    />

                  </Box>
                }

                {
                  activeRow.portType === "trunk" &&
                  !isProfileMode &&
                  <Box sx={rowStyle}>
                    <Typography
                      className={mainStyle["collapse-typography"]}
                      sx={{ alignSelf: "flex-start", pt: "6px" }}
                    >
                      Allow VLAN
                    </Typography>

                    <TextField
                      disabled={!showEditGeneral || !isEditable}
                      type="text"
                      placeholder="e.g. 1,4-7"
                      value={activeRow.allowVlan}
                      onChange={(e) =>
                        setActiveRow({
                          ...activeRow,
                          allowVlan: e.target.value,
                        })
                      }
                      sx={isValidInputStyles(
                        checkAllowVlans(activeRow.allowVlan?.trim())
                      )}
                      helperText={`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                    />
                  </Box>
                }

                {
                  !isProfileMode &&
                  vlanIdsCount.number > 128 &&
                  <>
                    <Box
                      sx={{
                        ...rowStyle,
                        marginTop: "-1.3rem"
                      }}
                    >
                      <Typography
                        className={mainStyle["collapse-typography"]}
                        sx={{ alignSelf: "flex-start", pt: "6px" }}
                      >
                      </Typography>
                      <Typography
                        sx={{
                          color: theme.palette.primary.redPrimary,
                          fontSize: "0.75rem",
                        }}
                      >
                        Limit reached: Maximum 128 VLANs allowed.
                      </Typography>

                    </Box>
                  </>
                }

                {/* Link Speed */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    gap: 2,
                  }}
                >

                  {/* 上排：s ( Fiber )*/}
                  {
                    isFiber &&
                    (
                      <>
                        {
                          isProfileMode &&
                          <Box sx={rowStyle}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              {/* {`${linkSpeedLabel}Link Speed`} */}
                              Port Speed
                            </Typography>
                            <MenuSelect
                              disabled
                              value={selectedProfile?.sfpSpeed}
                              options={linkSpeedOptions}
                              width="100%"
                            />
                          </Box>
                        }
                        {
                          !isProfileMode && supportedSpeed.s.length > 0 &&
                          <Box sx={rowStyle}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              {/* {`${linkSpeedLabel}Link Speed`} */}
                              Port Speed
                            </Typography>
                            <MenuSelect
                              disabled={!showEditGeneral || !isEditable}
                              value={activeRow.speedS || ""}
                              onChange={(opt) =>
                                setActiveRow({
                                  ...activeRow,
                                  speedS: opt.value,
                                })
                              }
                              options={supportedSpeed.s}
                              width="100%"
                            />
                          </Box>
                        }
                      </>
                    )
                  }

                  {/* 下排：t ( Copper ) */}
                  {
                    isCopper && (
                      <>
                        {
                          isProfileMode &&
                          <Box sx={rowStyle}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              {/* {`${linkSpeedLabel}Link Speed`} */}
                              Port Speed
                            </Typography>
                            <MenuSelect
                              disabled
                              value={selectedProfile?.speed}
                              options={linkSpeedOptions}
                              width="100%"
                            />
                          </Box>
                        }

                        {
                          !isProfileMode && supportedSpeed.t.length > 0 &&
                          <Box sx={rowStyle}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              {/* {`${linkSpeedLabel}Link Speed`} */}
                              Port Speed
                            </Typography>
                            <MenuSelect
                              disabled={!showEditGeneral || !isEditable}
                              value={activeRow.speedT || ""}
                              onChange={(opt) =>
                                setActiveRow({
                                  ...activeRow,
                                  speedT: opt.value,
                                })
                              }
                              options={supportedSpeed.t}
                              width="100%"
                            />
                          </Box>
                        }
                      </>
                    )
                  }
                </Box>
              </>
            )}
          </Box>
        </CollapseCard>

        {
          isSelectedPortSupportPoE &&
          <CollapseCard title="PoE">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]}>
                  PoE Status
                </Typography>

                {
                  isProfileMode &&
                  <RadioGroup
                    disabled
                    row
                    value={selectedProfile?.poeState ? "enable" : "disable"}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {["Enable", "Disable"].map((option, idx) => (
                      <RadioButton
                        key={"profilePoeStatusRadioButton" + idx}
                        label={option}
                        name="profilePoeStatusRadio"
                        value={option.toLowerCase()}
                        disabled
                      />
                    ))}
                  </RadioGroup>
                }

                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    value={activeRow?.poeState ? "enable" : "disable"}
                    onChange={(e) => {
                      const newValue = e.target.value === "enable";
                      setActiveRow((prev) => ({
                        ...prev,
                        poeState: newValue,
                      }));
                    }}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {["Enable", "Disable"].map((option, idx) => (
                      <RadioButton
                        key={"poeStatusRadioButton" + idx}
                        label={option}
                        name="poeStatusRadio"
                        value={option.toLowerCase()}
                        disabled={!showEditGeneral || !isEditable}
                      />
                    ))}
                  </RadioGroup>
                }
              </Box>
            </Box>
          </CollapseCard>
        }
        <CollapseCard title="Advance">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={rowStyle}>
              <Typography className={mainStyle["collapse-typography"]}>
                DDP
              </Typography>

              {
                isProfileMode &&
                <RadioGroup
                  disabled
                  row
                  value={selectedProfile?.ddpState ? "enable" : "disable"}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "80px auto",
                    columnGap: "20px",
                  }}
                >
                  {["Enable", "Disable"].map((option, idx) => (
                    <RadioButton
                      key={"profileDdpRadioButton" + idx}
                      label={option}
                      name="profileDdpRadio"
                      value={option.toLowerCase()}
                      disabled
                    />
                  ))}
                </RadioGroup>
              }

              {
                !isProfileMode &&
                <RadioGroup
                  row
                  value={activeRow?.ddpState ? "enable" : "disable"}
                  onChange={(e) => {
                    const newValue = e.target.value === "enable";
                    setActiveRow((prev) => ({
                      ...prev,
                      ddpState: newValue,
                    }));
                  }}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "80px auto",
                    columnGap: "20px",
                  }}
                >
                  {["Enable", "Disable"].map((option, idx) => (
                    <RadioButton
                      key={"ddpRadioButton" + idx}
                      label={option}
                      name="ddpRadio"
                      value={option.toLowerCase()}
                      disabled={!showEditGeneral || !isEditable}
                    />
                  ))}
                </RadioGroup>
              }
            </Box>

            <Box sx={rowStyle}>
              <Typography className={mainStyle["collapse-typography"]}>
                LBD
              </Typography>

              {
                isProfileMode &&
                <RadioGroup
                  disabled
                  row
                  value={selectedProfile?.lbdState ? "enable" : "disable"}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "80px auto",
                    columnGap: "20px",
                  }}
                >
                  {["Enable", "Disable"].map((option, idx) => (
                    <RadioButton
                      key={"profileLbdRadioButton" + idx}
                      label={option}
                      name="profileLbdRadio"
                      value={option.toLowerCase()}
                      disabled
                    />
                  ))}
                </RadioGroup>
              }

              {
                !isProfileMode &&
                <RadioGroup
                  row
                  value={activeRow?.lbdState ? "enable" : "disable"}
                  onChange={(e) => {
                    const newValue = e.target.value === "enable";
                    setActiveRow((prev) => ({
                      ...prev,
                      lbdState: newValue,
                    }));
                  }}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "80px auto",
                    columnGap: "20px",
                  }}
                >
                  {["Enable", "Disable"].map((option, idx) => (
                    <RadioButton
                      key={"lbdRadioButton" + idx}
                      label={option}
                      name="lbdRadio"
                      value={option.toLowerCase()}
                      disabled={!showEditGeneral || !isEditable}
                    />
                  ))}
                </RadioGroup>
              }
            </Box>
          </Box>
        </CollapseCard>
        {isChanged && (
          <Box
            sx={{
              position: "sticky",
              width: "107%",
              display: "flex",
              justifyContent: "center",
              padding: "16px",
              backgroundColor: "primary.lightBlue",
              bottom: 0,
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
          >
            <OutlineButton
              onClick={handleCancel}
              sx={{ marginRight: "20px" }}
              width="100px"
              height="32px"
            >
              Revert
            </OutlineButton>
            <SolidButton
              disabled={!checkFormValid()}
              onClick={onSave}
              width="100px"
              height="32px"
            >
              Save
            </SolidButton>
          </Box>
        )}
      </Box>
    </>
  );
};

export default SettingsDrawer;
